// frontend/src/components/ProfileModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Tabs, Tab, Spinner, Table, Row, Col, Card } from 'react-bootstrap';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

// 中国主要银行列表
const BANK_OPTIONS = [
  { value: '中国工商银行', label: '中国工商银行' },
  { value: '中国农业银行', label: '中国农业银行' },
  { value: '中国银行', label: '中国银行' },
  { value: '中国建设银行', label: '中国建设银行' },
  { value: '交通银行', label: '交通银行' },
  { value: '中国邮政储蓄银行', label: '中国邮政储蓄银行' },
  { value: '招商银行', label: '招商银行' },
  { value: '中信银行', label: '中信银行' },
  { value: '浦发银行', label: '浦发银行' },
  { value: '兴业银行', label: '兴业银行' },
  { value: '光大银行', label: '光大银行' },
  { value: '民生银行', label: '民生银行' },
  { value: '华夏银行', label: '华夏银行' },
  { value: '平安银行', label: '平安银行' },
  { value: '广发银行', label: '广发银行' },
  { value: '浙商银行', label: '浙商银行' },
  { value: '渤海银行', label: '渤海银行' },
  { value: '其他银行', label: '其他银行' },
];

const ProfileModal = ({ show, onHide }) => {
  const { user, refreshUserInfo } = useAuth();
  
  // 修改密码表单状态
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  
  // 提现表单状态
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [useExistingBankCard, setUseExistingBankCard] = useState(true);
  const [selectedBankCardId, setSelectedBankCardId] = useState('');
  const [bankInfo, setBankInfo] = useState({
    bankName: '',
    cardNumber: '',
    accountName: '',
    branchName: ''
  });
  const [withdrawalMessage, setWithdrawalMessage] = useState({ type: '', text: '' });
  const [isWithdrawalLoading, setIsWithdrawalLoading] = useState(false);
  
  // 提现历史列表
  const [withdrawals, setWithdrawals] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  // 银行卡管理状态
  const [bankCards, setBankCards] = useState([]);
  const [isLoadingBankCards, setIsLoadingBankCards] = useState(false);
  const [showAddBankCard, setShowAddBankCard] = useState(false);
  const [newBankCard, setNewBankCard] = useState({
    bankName: '',
    cardNumber: '',
    accountName: '',
    branchName: ''
  });
  const [bankCardMessage, setBankCardMessage] = useState({ type: '', text: '' });
  const [isAddingBankCard, setIsAddingBankCard] = useState(false);
  
  // 账目系统状态
  const [gameHistory, setGameHistory] = useState([]);
  const [allGameHistory, setAllGameHistory] = useState([]); // 新增：存储全部游戏历史用于K线图
  const [isLoadingGameHistory, setIsLoadingGameHistory] = useState(false);
  const [gameStats, setGameStats] = useState({
    totalGames: 0,
    wins: 0,
    losses: 0,
    totalProfit: 0,
    winRate: 0,
    maxWin: 0,
    maxLoss: 0,
    avgProfit: 0,
    winStreak: 0,
    lossStreak: 0
  });
  const [gameHistoryPage, setGameHistoryPage] = useState(1);
  const [gameHistoryTotalPages, setGameHistoryTotalPages] = useState(1);
  const gameHistoryLimit = 10;
  
  // K线图分页状态
  const [kLineCurrentPage, setKLineCurrentPage] = useState(1);
  const kLinePageSize = 25; // 每页显示25条数据，让K线图更加美观连贯
  
  // 触摸滑动状态
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // K线图翻页处理 - 使用 useCallback 和函数式更新（必须在 useEffect 之前定义）
  const handleKLinePageChange = useCallback((direction) => {
    const totalPages = Math.ceil(allGameHistory.length / kLinePageSize);
    
    setKLineCurrentPage(prevPage => {
      if (direction === 'prev' && prevPage > 1) {
        return prevPage - 1;
      } else if (direction === 'next' && prevPage < totalPages) {
        return prevPage + 1;
      }
      return prevPage;
    });
  }, [allGameHistory.length, kLinePageSize]);
  
  // 清空所有表单和消息
  const resetForms = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setWithdrawalAmount('');
    setUseExistingBankCard(true);
    setSelectedBankCardId('');
    setBankInfo({
      bankName: '',
      cardNumber: '',
      accountName: '',
      branchName: ''
    });
    setPasswordMessage({ type: '', text: '' });
    setWithdrawalMessage({ type: '', text: '' });
    setBankCardMessage({ type: '', text: '' });
    setShowAddBankCard(false);
    setNewBankCard({
      bankName: '',
      cardNumber: '',
      accountName: '',
      branchName: ''
    });
  };
  
  // 当模态框显示时获取提现历史和银行卡列表
  useEffect(() => {
    if (show) {
      fetchWithdrawalHistory();
      fetchBankCards();
      fetchGameHistory();
    } else {
      resetForms();
    }
  }, [show]); // 移除 kLineCurrentPage 依赖，避免无限循环
  
  // 单独的 useEffect 处理键盘事件
  useEffect(() => {
    if (!show) return;
    
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        handleKLinePageChange('prev');
      } else if (e.key === 'ArrowRight') {
        handleKLinePageChange('next');
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [show, handleKLinePageChange]); // 依赖于 memoized 的处理函数
  
  // 获取提现历史
  const fetchWithdrawalHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const res = await api.get('/api/withdrawals/my-withdrawals');
      setWithdrawals(res.data);
    } catch (error) {
      console.error('获取提现历史失败:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };
  
  // 获取游戏历史和统计
  const fetchGameHistory = async (page = 1) => {
    setIsLoadingGameHistory(true);
    try {
      // 获取分页数据用于显示
      const res = await api.get(`/api/users/${user._id}/round-logs?page=${page}&limit=${gameHistoryLimit}`);
      const logs = res.data.logs || [];
      
      setGameHistory(logs);
      setGameHistoryPage(res.data.currentPage || 1);
      setGameHistoryTotalPages(res.data.totalPages || 1);
      
      // 获取全部数据用于统计计算
      if (page === 1) { // 只在第一次加载时获取全部数据进行统计
        fetchAllGameStatsData();
      }
    } catch (error) {
      console.error('获取游戏历史失败:', error);
    } finally {
      setIsLoadingGameHistory(false);
    }
  };

  // 获取全部游戏数据用于统计
  const fetchAllGameStatsData = async () => {
    try {
      // 获取全部数据（不分页）用于统计
      const allDataRes = await api.get(`/api/users/${user._id}/round-logs?page=1&limit=10000`);
      const allLogs = allDataRes.data.logs || [];
      
      // 存储全部数据用于K线图
      setAllGameHistory(allLogs);
      
      // 重置K线图页码到第一页
      setKLineCurrentPage(1);
      
      // 计算统计数据
      if (allLogs.length > 0) {
        calculateGameStats(allLogs);
      } else {
        // 如果没有数据，重置统计
        setGameStats({
          totalGames: 0,
          wins: 0,
          losses: 0,
          totalProfit: 0,
          winRate: 0,
          maxWin: 0,
          maxLoss: 0,
          avgProfit: 0,
          winStreak: 0,
          lossStreak: 0
        });
      }
    } catch (error) {
      console.error('获取全部游戏统计数据失败:', error);
    }
  };
  
  // 计算游戏统计数据
  const calculateGameStats = (logs) => {
    const totalGames = logs.length;
    const wins = logs.filter(log => (log.netProfit || 0) > 0).length;
    const losses = logs.filter(log => (log.netProfit || 0) < 0).length;
    const totalProfit = logs.reduce((sum, log) => sum + (log.netProfit || 0), 0);
    const winRate = totalGames > 0 ? ((wins / totalGames) * 100) : 0;
    
    const profits = logs.map(log => log.netProfit || 0);
    const maxWin = profits.length > 0 ? Math.max(...profits) : 0;
    const maxLoss = profits.length > 0 ? Math.min(...profits) : 0;
    const avgProfit = totalGames > 0 ? (totalProfit / totalGames) : 0;
    
    // 计算连胜连败纪录
    let maxWinStreak = 0;
    let maxLossStreak = 0;
    let currentWinStreak = 0;
    let currentLossStreak = 0;
    
    logs.forEach(log => {
      const profit = log.netProfit || 0;
      if (profit > 0) {
        currentWinStreak++;
        currentLossStreak = 0;
        maxWinStreak = Math.max(maxWinStreak, currentWinStreak);
      } else if (profit < 0) {
        currentLossStreak++;
        currentWinStreak = 0;
        maxLossStreak = Math.max(maxLossStreak, currentLossStreak);
      } else {
        currentWinStreak = 0;
        currentLossStreak = 0;
      }
    });
    
    setGameStats({
      totalGames,
      wins,
      losses,
      totalProfit,
      winRate: winRate.toFixed(2),
      maxWin,
      maxLoss,
      avgProfit,
      winStreak: maxWinStreak,
      lossStreak: maxLossStreak
    });
  };
  
  // 生成K线图数据（支持分页）
  const generateKLineData = () => {
    if (!allGameHistory.length) return []; // 使用全部游戏历史数据
    
    let runningBalance = user?.currentPoints || 0;
    const allKLineData = [];
    
    // 从最新的记录开始，向前计算余额
    for (let i = allGameHistory.length - 1; i >= 0; i--) {
      const log = allGameHistory[i];
      const profit = log.netProfit || 0;
      const balanceAfter = runningBalance;
      const balanceBefore = runningBalance - profit;
      
      allKLineData.unshift({
        period: allGameHistory.length - i,
        timestamp: log.startTime,
        open: balanceBefore,
        close: balanceAfter,
        high: Math.max(balanceBefore, balanceAfter),
        low: Math.min(balanceBefore, balanceAfter),
        profit: profit,
        deviceName: log.deviceName || '未知设备'
      });
      
      runningBalance = balanceBefore;
    }
    
    // 分页逻辑：根据当前页码返回对应的数据
    const startIndex = (kLineCurrentPage - 1) * kLinePageSize;
    const endIndex = startIndex + kLinePageSize;
    return allKLineData.slice(startIndex, endIndex);
  };
  
  // 获取K线图总页数
  const getKLineTotalPages = () => {
    if (!allGameHistory.length) return 1;
    return Math.ceil(allGameHistory.length / kLinePageSize);
  };
  
  // 触摸滑动处理
  const minSwipeDistance = 50; // 最小滑动距离
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      // 左滑 - 下一页
      handleKLinePageChange('next');
    } else if (isRightSwipe) {
      // 右滑 - 上一页
      handleKLinePageChange('prev');
    }
  };
  
  // 处理修改密码提交
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // 表单验证
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({ type: 'danger', text: '请填写所有密码字段' });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'danger', text: '新密码与确认密码不匹配' });
      return;
    }
    
    setIsPasswordLoading(true);
    try {
      const res = await api.post('/api/users/change-password', {
        currentPassword,
        newPassword
      });
      
      setPasswordMessage({ type: 'success', text: res.data.message || '密码修改成功' });
      // 清空表单
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordMessage({
        type: 'danger',
        text: error.response?.data?.message || '密码修改失败，请重试'
      });
    } finally {
      setIsPasswordLoading(false);
    }
  };
  
  // 处理提现申请提交
  const handleWithdrawal = async (e) => {
    e.preventDefault();
    
    // 验证金额
    const amount = parseFloat(withdrawalAmount);
    if (isNaN(amount) || amount <= 0) {
      setWithdrawalMessage({ type: 'danger', text: '请输入有效的提现金额' });
      return;
    }
    
    if (amount > user.currentPoints) {
      setWithdrawalMessage({ type: 'danger', text: '提现金额不能超过当前积分' });
      return;
    }
    
    let finalBankInfo;
    
    if (useExistingBankCard) {
      // 使用已绑定的银行卡
      if (!selectedBankCardId) {
        setWithdrawalMessage({ type: 'danger', text: '请选择一张银行卡' });
        return;
      }
      
      const selectedCard = bankCards.find(card => card.id === selectedBankCardId);
      if (!selectedCard) {
        setWithdrawalMessage({ type: 'danger', text: '所选银行卡不存在' });
        return;
      }
      
      finalBankInfo = {
        bankName: selectedCard.bankName,
        cardNumber: selectedCard.cardNumber,
        accountName: selectedCard.accountName,
        branchName: selectedCard.branchName
      };
    } else {
      // 使用新的银行卡信息
      if (!bankInfo.bankName) {
        setWithdrawalMessage({ type: 'danger', text: '请选择银行' });
        return;
      }
      
      if (!bankInfo.cardNumber) {
        setWithdrawalMessage({ type: 'danger', text: '请输入银行卡号' });
        return;
      }
      
      if (!bankInfo.accountName) {
        setWithdrawalMessage({ type: 'danger', text: '请输入持卡人姓名' });
        return;
      }
      
      if (!bankInfo.branchName) {
        setWithdrawalMessage({ type: 'danger', text: '请输入开户行' });
        return;
      }
      
      finalBankInfo = bankInfo;
    }
    
    setIsWithdrawalLoading(true);
    try {
      const res = await api.post('/api/withdrawals', { 
        amount,
        bankInfo: finalBankInfo
      });
      
      setWithdrawalMessage({ type: 'success', text: res.data.message || '提现申请已提交' });
      setWithdrawalAmount('');
      setSelectedBankCardId('');
      setBankInfo({
        bankName: '',
        cardNumber: '',
        accountName: '',
        branchName: ''
      });
      refreshUserInfo(); // 刷新用户信息，更新积分显示
      fetchWithdrawalHistory(); // 刷新提现历史
    } catch (error) {
      setWithdrawalMessage({
        type: 'danger',
        text: error.response?.data?.message || '提现申请失败，请重试'
      });
    } finally {
      setIsWithdrawalLoading(false);
    }
  };
  
  // 获取提现状态中文显示
  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return '处理中';
      case 'approved': return '已通过';
      case 'rejected': return '已拒绝';
      default: return '未知状态';
    }
  };
  
  // 获取状态对应的样式类
  const getStatusClass = (status) => {
    switch(status) {
      case 'pending': return 'text-warning';
      case 'approved': return 'text-success';
      case 'rejected': return 'text-danger';
      default: return '';
    }
  };
   // 格式化时间显示
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN');
  };
  
  // 获取银行卡列表
  const fetchBankCards = async () => {
    setIsLoadingBankCards(true);
    try {
      const res = await api.get('/api/users/bank-cards');
      setBankCards(res.data.bankCards || []);
    } catch (error) {
      console.error('获取银行卡列表失败:', error);
    } finally {
      setIsLoadingBankCards(false);
    }
  };
  
  // 添加银行卡
  const handleAddBankCard = async (e) => {
    e.preventDefault();
    
    // 验证银行卡信息
    if (!newBankCard.bankName) {
      setBankCardMessage({ type: 'danger', text: '请选择银行' });
      return;
    }
    
    if (!newBankCard.cardNumber) {
      setBankCardMessage({ type: 'danger', text: '请输入银行卡号' });
      return;
    }
    
    if (!newBankCard.accountName) {
      setBankCardMessage({ type: 'danger', text: '请输入持卡人姓名' });
      return;
    }
    
    if (!newBankCard.branchName) {
      setBankCardMessage({ type: 'danger', text: '请输入开户行' });
      return;
    }
    
    setIsAddingBankCard(true);
    try {
      console.log('准备发送银行卡数据:', newBankCard);
      const res = await api.post('/api/users/bank-cards', newBankCard);
      console.log('银行卡添加成功:', res.data);
      setBankCardMessage({ type: 'success', text: '银行卡添加成功' });
      setNewBankCard({
        bankName: '',
        cardNumber: '',
        accountName: '',
        branchName: ''
      });
      setShowAddBankCard(false);
      fetchBankCards(); // 刷新银行卡列表
    } catch (error) {
      console.error('添加银行卡失败:', error);
      console.error('错误响应:', error.response);
      console.error('错误详情:', error.response?.data);
      
      let errorMessage = '添加银行卡失败，请重试';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = '请求数据格式错误，请检查银行卡信息';
      }
      
      setBankCardMessage({
        type: 'danger',
        text: errorMessage
      });
    } finally {
      setIsAddingBankCard(false);
    }
  };
  
  // 删除银行卡
  const handleDeleteBankCard = async (cardId) => {
    if (!window.confirm('确定要删除这张银行卡吗？')) {
      return;
    }
    
    try {
      await api.delete(`/api/users/bank-cards/${cardId}`);
      setBankCardMessage({ type: 'success', text: '银行卡删除成功' });
      fetchBankCards(); // 刷新银行卡列表
    } catch (error) {
      setBankCardMessage({
        type: 'danger',
        text: error.response?.data?.message || '删除银行卡失败，请重试'
      });
    }
  };
  
  // 设置默认银行卡
  const handleSetDefaultBankCard = async (cardId) => {
    try {
      await api.put(`/api/users/bank-cards/${cardId}/set-default`);
      setBankCardMessage({ type: 'success', text: '已设置为默认银行卡' });
      fetchBankCards(); // 刷新银行卡列表
    } catch (error) {
      setBankCardMessage({
        type: 'danger',
        text: error.response?.data?.message || '设置默认银行卡失败，请重试'
      });
    }
  };
  
  // 获取银行卡号显示（脱敏）
  const getMaskedCardNumber = (cardNumber) => {
    if (!cardNumber || cardNumber.length < 8) return cardNumber;
    const start = cardNumber.slice(0, 4);
    const end = cardNumber.slice(-4);
    return `${start}****${end}`;
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      backdrop="static" 
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>个人信息</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <h4>欢迎，{user?.username}</h4>
          <p>当前积分：<strong>{user?.currentPoints || 0}</strong></p>
          <p>银币余额：<strong>{user?.silverCoins || 0}</strong></p>
        </div>
        
        <Tabs defaultActiveKey="password" id="profile-tabs" className="mb-3">
          {/* 修改密码选项卡 */}
          <Tab eventKey="password" title="修改密码">
            <Form onSubmit={handlePasswordChange}>
              {passwordMessage.text && (
                <Alert variant={passwordMessage.type} className="mb-3">
                  {passwordMessage.text}
                </Alert>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>当前密码</Form.Label>
                <Form.Control 
                  type="password" 
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="请输入当前密码"
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>新密码</Form.Label>
                <Form.Control 
                  type="password" 
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="请输入新密码"
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>确认新密码</Form.Label>
                <Form.Control 
                  type="password" 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="请再次输入新密码"
                  required
                />
              </Form.Group>
              
              <div className="d-flex justify-content-end">
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={isPasswordLoading}
                >
                  {isPasswordLoading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      处理中...
                    </>
                  ) : '修改密码'}
                </Button>
              </div>
            </Form>
          </Tab>
          
          {/* 账目系统选项卡 */}
          <Tab eventKey="gameStats" title="输赢账目">
            <div className="mt-3">
              {/* 统计卡片 */}
              <Row className="mb-4">
                <Col md={6}>
                  <Card className="h-100 text-center">
                    <Card.Body>
                      <h5 className="text-primary">当前余额</h5>
                      <h3 className={user?.currentPoints >= 0 ? 'text-success' : 'text-danger'}>
                        ¥{(user?.currentPoints || 0).toFixed(2)}
                      </h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100 text-center">
                    <Card.Body>
                      <h5 className="text-primary">总盈亏</h5>
                      <h3 className={gameStats.totalProfit >= 0 ? 'text-success' : 'text-danger'}>
                        {gameStats.totalProfit >= 0 ? '+' : ''}¥{gameStats.totalProfit.toFixed(2)}
                      </h3>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* 详细统计 */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h6>胜率</h6>
                      <h4 className={gameStats.winRate >= 50 ? 'text-success' : 'text-danger'}>
                        {gameStats.winRate}%
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h6>总场次</h6>
                      <h4>{gameStats.totalGames}</h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h6>胜场</h6>
                      <h4 className="text-success">{gameStats.wins}</h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h6>败场</h6>
                      <h4 className="text-danger">{gameStats.losses}</h4>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h6>单次最大盈利</h6>
                      <h4 className="text-success">¥{gameStats.maxWin.toFixed(2)}</h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h6>单次最大亏损</h6>
                      <h4 className="text-danger">¥{gameStats.maxLoss.toFixed(2)}</h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h6>平均盈亏</h6>
                      <h4 className={gameStats.avgProfit >= 0 ? 'text-success' : 'text-danger'}>
                        ¥{gameStats.avgProfit.toFixed(2)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* K线图 */}
              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
                  <h5 className="mb-0">盈亏走势图</h5>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <small className="text-muted">
                      第 {kLineCurrentPage} / {getKLineTotalPages()} 页 (每页{kLinePageSize}条)
                    </small>
                    {getKLineTotalPages() > 1 && (
                      <>
                        <small className="text-muted d-block d-sm-none">
                          可左右滑动切换
                        </small>
                        {getKLineTotalPages() > 3 && (
                          <div className="d-flex align-items-center gap-1">
                            <small className="text-muted">跳转:</small>
                            <Form.Select 
                              size="sm" 
                              style={{ width: 'auto', minWidth: '80px' }}
                              value={kLineCurrentPage}
                              onChange={(e) => setKLineCurrentPage(parseInt(e.target.value))}
                            >
                              {Array.from({ length: getKLineTotalPages() }, (_, i) => (
                                <option key={i + 1} value={i + 1}>第{i + 1}页</option>
                              ))}
                            </Form.Select>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  {allGameHistory.length === 0 ? (
                    <div className="text-center py-4">
                      <Alert variant="info">
                        <i className="bi bi-info-circle me-2"></i>
                        暂无游戏记录，无法显示走势图
                      </Alert>
                    </div>
                  ) : (
                    <>
                      <div 
                        style={{ 
                          width: '100%', 
                          height: 300,
                          cursor: getKLineTotalPages() > 1 ? 'grab' : 'default',
                          userSelect: 'none'
                        }}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                      >
                        <ResponsiveContainer>
                          <LineChart data={generateKLineData()}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="period" 
                              label={{ value: '游戏轮次', position: 'insideBottom', offset: -5 }}
                            />
                            <YAxis label={{ value: '积分余额', angle: -90, position: 'insideLeft' }} />
                            <Tooltip 
                              formatter={(value, name) => [
                                `¥${value.toFixed(2)}`, 
                                name === 'close' ? '游戏后余额' : name
                              ]}
                              labelFormatter={(label) => `第${label}轮游戏`}
                              content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div className="bg-white p-3 border rounded shadow">
                                      <p className="mb-1"><strong>第{label}轮游戏</strong></p>
                                      <p className="mb-1">设备: {data.deviceName}</p>
                                      <p className="mb-1">游戏前: ¥{data.open.toFixed(2)}</p>
                                      <p className="mb-1">游戏后: ¥{data.close.toFixed(2)}</p>
                                      <p className="mb-0" style={{color: data.profit >= 0 ? 'green' : 'red'}}>
                                        盈亏: {data.profit >= 0 ? '+' : ''}¥{data.profit.toFixed(2)}
                                      </p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="close" 
                              stroke="#8884d8" 
                              strokeWidth={2}
                              dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      
                      {/* K线图分页控制 */}
                      {getKLineTotalPages() > 1 && (
                        <div className="d-flex flex-column align-items-center mt-3 gap-2">
                          <div className="d-flex justify-content-center align-items-center gap-3">
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              disabled={kLineCurrentPage <= 1}
                              onClick={() => handleKLinePageChange('prev')}
                            >
                              <i className="bi bi-chevron-left me-1"></i>
                              上一页
                            </Button>
                            
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-muted">第</span>
                              <span className="fw-bold text-primary">{kLineCurrentPage}</span>
                              <span className="text-muted">/ {getKLineTotalPages()} 页</span>
                            </div>
                            
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              disabled={kLineCurrentPage >= getKLineTotalPages()}
                              onClick={() => handleKLinePageChange('next')}
                            >
                              下一页
                              <i className="bi bi-chevron-right ms-1"></i>
                            </Button>
                          </div>
                          
                          {/* 桌面端键盘快捷键提示 */}
                          <div className="text-center d-none d-sm-block">
                            <small className="text-muted">
                              <i className="bi bi-keyboard me-1"></i>
                              使用 ← → 键快速切换页面
                            </small>
                          </div>
                        </div>
                      )}
                      
                      {/* 移动端滑动提示 */}
                      {getKLineTotalPages() > 1 && (
                        <div className="text-center mt-2 d-block d-sm-none">
                          <small className="text-muted">
                            <i className="bi bi-hand-index me-1"></i>
                            左右滑动查看更多数据
                          </small>
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>

              {/* 游戏记录详情 */}
              <Card>
                <Card.Header>
                  <h5 className="mb-0">游戏记录</h5>
                </Card.Header>
                <Card.Body>
                  {isLoadingGameHistory ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" />
                      <p className="mt-2">加载中...</p>
                    </div>
                  ) : gameHistory.length === 0 ? (
                    <Alert variant="info">
                      <i className="bi bi-info-circle me-2"></i>
                      暂无游戏记录
                    </Alert>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table striped bordered hover size="sm">
                          <thead>
                            <tr>
                              <th>时间</th>
                              <th>设备</th>
                              <th>投注</th>
                              <th>返还</th>
                              <th>盈亏</th>
                              <th>状态</th>
                            </tr>
                          </thead>
                          <tbody>
                            {gameHistory.map((record, index) => (
                              <tr key={record._id || index}>
                                <td>
                                  <small>{new Date(record.startTime).toLocaleString()}</small>
                                </td>
                                <td>{record.deviceName || '未知设备'}</td>
                                <td>¥{(record.betTotal || 0).toFixed(2)}</td>
                                <td>¥{(record.payoutTotal || 0).toFixed(2)}</td>
                                <td>
                                  <span style={{ 
                                    color: (record.netProfit || 0) >= 0 ? 'green' : 'red',
                                    fontWeight: 'bold'
                                  }}>
                                    {(record.netProfit || 0) >= 0 ? '+' : ''}¥{(record.netProfit || 0).toFixed(2)}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${(record.netProfit || 0) >= 0 ? 'bg-success' : 'bg-danger'}`}>
                                    {(record.netProfit || 0) >= 0 ? '盈利' : '亏损'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      
                      {/* 分页控制 */}
                      {gameHistoryTotalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            disabled={gameHistoryPage <= 1}
                            onClick={() => fetchGameHistory(gameHistoryPage - 1)}
                          >
                            上一页
                          </Button>
                          <span>第 {gameHistoryPage} / {gameHistoryTotalPages} 页</span>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            disabled={gameHistoryPage >= gameHistoryTotalPages}
                            onClick={() => fetchGameHistory(gameHistoryPage + 1)}
                          >
                            下一页
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </div>
          </Tab>
          
          {/* 申请提现选项卡 */}
          <Tab eventKey="withdrawal" title="申请提现">
            <Form onSubmit={handleWithdrawal}>
              {withdrawalMessage.text && (
                <Alert variant={withdrawalMessage.type} className="mb-3">
                  {withdrawalMessage.text}
                </Alert>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>提现金额</Form.Label>
                <Form.Control 
                  type="number" 
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  placeholder="请输入提现积分数量"
                  required
                  min="1"
                  max={user?.currentPoints || 0}
                />
                <Form.Text className="text-muted">
                  当前可提现积分：{user?.currentPoints || 0}
                </Form.Text>
              </Form.Group>
              
              <div className="mt-4 mb-3 bank-info-section p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <h5 className="mb-3 text-primary">银行卡信息</h5>
                
                {/* 银行卡选择方式 */}
                <Form.Group className="mb-4">
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="use-existing-card"
                      name="bank-card-option"
                      label="使用已绑定银行卡"
                      checked={useExistingBankCard}
                      onChange={() => setUseExistingBankCard(true)}
                    />
                    <Form.Check
                      type="radio"
                      id="use-new-card"
                      name="bank-card-option"
                      label="使用新银行卡"
                      checked={!useExistingBankCard}
                      onChange={() => setUseExistingBankCard(false)}
                    />
                  </div>
                </Form.Group>
                
                {/* 选择已绑定的银行卡 */}
                {useExistingBankCard && (
                  <div>
                    {bankCards.length === 0 ? (
                      <Alert variant="warning">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        您还没有绑定银行卡，请先在"银行卡管理"中添加银行卡，或选择"使用新银行卡"
                      </Alert>
                    ) : (
                      <Form.Group className="mb-3">
                        <Form.Label>选择银行卡</Form.Label>
                        <Form.Select 
                          value={selectedBankCardId} 
                          onChange={(e) => setSelectedBankCardId(e.target.value)}
                          required={useExistingBankCard}
                        >
                          <option value="">请选择一张银行卡</option>
                          {bankCards.map(card => (
                            <option key={card.id} value={card.id}>
                              {card.bankName} - {getMaskedCardNumber(card.cardNumber)} - {card.accountName}
                              {card.isDefault ? ' (默认)' : ''}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    )}
                  </div>
                )}
                
                {/* 输入新银行卡信息 */}
                {!useExistingBankCard && (
                  <div>
                    <Form.Group className="mb-3">
                      <Form.Label>选择银行</Form.Label>
                      <Form.Select 
                        value={bankInfo.bankName} 
                        onChange={(e) => setBankInfo({...bankInfo, bankName: e.target.value})}
                        required={!useExistingBankCard}
                        className="form-select-lg"
                      >
                        <option value="">请选择银行</option>
                        {BANK_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <i className="bi bi-credit-card me-1"></i>
                        银行卡号
                      </Form.Label>
                      <Form.Control 
                        type="text" 
                        value={bankInfo.cardNumber}
                        onChange={(e) => setBankInfo({...bankInfo, cardNumber: e.target.value.replace(/\s+/g, '')})}
                        placeholder="请输入银行卡号"
                        required={!useExistingBankCard}
                        pattern="[0-9]+"
                      />
                      <Form.Text className="text-muted">
                        请输入正确的银行卡号，不含空格或特殊字符
                      </Form.Text>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <i className="bi bi-person me-1"></i>
                        持卡人姓名
                      </Form.Label>
                      <Form.Control 
                        type="text" 
                        value={bankInfo.accountName}
                        onChange={(e) => setBankInfo({...bankInfo, accountName: e.target.value})}
                        placeholder="请输入持卡人姓名"
                        required={!useExistingBankCard}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <i className="bi bi-building me-1"></i>
                        开户行
                      </Form.Label>
                      <Form.Control 
                        type="text" 
                        value={bankInfo.branchName}
                        onChange={(e) => setBankInfo({...bankInfo, branchName: e.target.value})}
                        placeholder="请输入开户行（如：xx银行xx支行）"
                        required={!useExistingBankCard}
                      />
                      <Form.Text className="text-muted">
                        请填写完整的开户银行支行名称
                      </Form.Text>
                    </Form.Group>
                  </div>
                )}
              </div>
              
              <Alert variant="info" className="my-3">
                <Alert.Heading>提现须知</Alert.Heading>
                <p>1. 提交申请后，管理员将在1-3个工作日内处理您的提现申请</p>
                <p>2. 请确保填写的银行卡信息准确无误，否则可能导致提现失败</p>
                <p>3. 提现申请提交后积分将被冻结，审核被拒绝后会退回积分</p>
              </Alert>

              <div className="d-flex justify-content-end mt-4">
                <Button 
                  variant="secondary" 
                  className="me-2"
                  onClick={() => {
                    setWithdrawalAmount('');
                    setSelectedBankCardId('');
                    setBankInfo({
                      bankName: '',
                      cardNumber: '',
                      accountName: '',
                      branchName: ''
                    });
                    setUseExistingBankCard(true);
                  }}
                >
                  重置
                </Button>
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={isWithdrawalLoading}
                >
                  {isWithdrawalLoading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      处理中...
                    </>
                  ) : '提交提现申请'}
                </Button>
              </div>
            </Form>
          </Tab>
          
          {/* 提现历史选项卡 */}
          <Tab eventKey="history" title="提现历史">
            {isLoadingHistory ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">加载中...</p>
              </div>
            ) : withdrawals.length === 0 ? (
              <Alert variant="info">
                暂无提现记录
              </Alert>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>提现金额</th>
                      <th>银行信息</th>
                      <th>申请时间</th>
                      <th>状态</th>
                      <th>处理时间</th>
                      <th>备注</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map(item => (
                      <React.Fragment key={item._id}>
                        <tr>
                          <td>{item.amount}</td>
                          <td>
                            {item.bankInfo ? (
                              <Button 
                                variant="link" 
                                size="sm"
                                className="p-0 text-decoration-none"
                                onClick={() => {
                                  // 使用简单的本地状态切换详情的显示
                                  const element = document.getElementById(`bank-info-${item._id}`);
                                  if (element) {
                                    element.style.display = element.style.display === "none" ? "table-row" : "none";
                                  }
                                }}
                              >
                                {item.bankInfo.bankName} <span className="text-muted">(点击查看详情)</span>
                              </Button>
                            ) : '无银行信息'}
                          </td>
                          <td>{formatDate(item.createdAt)}</td>
                          <td className={getStatusClass(item.status)}>
                            {getStatusText(item.status)}
                          </td>
                          <td>{item.reviewedAt ? formatDate(item.reviewedAt) : '-'}</td>
                          <td>{item.remarks || '-'}</td>
                        </tr>
                        {item.bankInfo && (
                          <tr id={`bank-info-${item._id}`} style={{ display: 'none', backgroundColor: '#f8f9fa' }}>
                            <td colSpan="6">
                              <div className="p-3">
                                <h6 className="mb-2">银行卡详细信息</h6>
                                <div><strong>银行:</strong> {item.bankInfo.bankName}</div>
                                <div><strong>卡号:</strong> {item.bankInfo.cardNumber}</div>
                                <div><strong>持卡人:</strong> {item.bankInfo.accountName}</div>
                                <div><strong>开户行:</strong> {item.bankInfo.branchName}</div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Tab>
          
          {/* 银行卡管理选项卡 */}
          <Tab eventKey="bankCards" title="银行卡管理">
            {bankCardMessage.text && (
              <Alert variant={bankCardMessage.type} className="mb-3">
                {bankCardMessage.text}
              </Alert>
            )}
            
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">我的银行卡</h5>
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setShowAddBankCard(true)}
              >
                <i className="bi bi-plus-circle me-1"></i>
                添加银行卡
              </Button>
            </div>
            
            {/* 添加银行卡表单 */}
            {showAddBankCard && (
              <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                <h6 className="mb-3">添加新银行卡</h6>
                <Form onSubmit={handleAddBankCard}>
                  <Form.Group className="mb-3">
                    <Form.Label>选择银行</Form.Label>
                    <Form.Select 
                      value={newBankCard.bankName} 
                      onChange={(e) => setNewBankCard({...newBankCard, bankName: e.target.value})}
                      required
                    >
                      <option value="">请选择银行</option>
                      {BANK_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>银行卡号</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newBankCard.cardNumber}
                      onChange={(e) => setNewBankCard({...newBankCard, cardNumber: e.target.value.replace(/\s+/g, '')})}
                      placeholder="请输入银行卡号"
                      required
                      pattern="[0-9]+"
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>持卡人姓名</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newBankCard.accountName}
                      onChange={(e) => setNewBankCard({...newBankCard, accountName: e.target.value})}
                      placeholder="请输入持卡人姓名"
                      required
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>开户行</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newBankCard.branchName}
                      onChange={(e) => setNewBankCard({...newBankCard, branchName: e.target.value})}
                      placeholder="请输入开户行（如：xx银行xx支行）"
                      required
                    />
                  </Form.Group>
                  
                  <div className="d-flex justify-content-end">
                    <Button 
                      variant="secondary" 
                      className="me-2"
                      onClick={() => {
                        setShowAddBankCard(false);
                        setNewBankCard({
                          bankName: '',
                          cardNumber: '',
                          accountName: '',
                          branchName: ''
                        });
                      }}
                    >
                      取消
                    </Button>
                    <Button 
                      variant="primary" 
                      type="submit"
                      disabled={isAddingBankCard}
                    >
                      {isAddingBankCard ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          添加中...
                        </>
                      ) : '添加银行卡'}
                    </Button>
                  </div>
                </Form>
              </div>
            )}
            
            {/* 银行卡列表 */}
            {isLoadingBankCards ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">加载中...</p>
              </div>
            ) : bankCards.length === 0 ? (
              <Alert variant="info">
                <i className="bi bi-info-circle me-2"></i>
                您还没有绑定银行卡，请先添加银行卡以便快速提现
              </Alert>
            ) : (
              <div className="row">
                {bankCards.map(card => (
                  <div key={card.id} className="col-md-6 mb-3">
                    <div className={`card ${card.isDefault ? 'border-primary' : ''}`}>
                      <div className="card-body">
                        {card.isDefault && (
                          <div className="badge bg-primary mb-2">默认银行卡</div>
                        )}
                        <h6 className="card-title">
                          <i className="bi bi-credit-card me-2"></i>
                          {card.bankName}
                        </h6>
                        <p className="card-text">
                          <small className="text-muted">卡号：{getMaskedCardNumber(card.cardNumber)}</small><br/>
                          <small className="text-muted">持卡人：{card.accountName}</small><br/>
                          <small className="text-muted">开户行：{card.branchName}</small>
                        </p>
                        <div className="d-flex justify-content-between">
                          {!card.isDefault && (
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleSetDefaultBankCard(card.id)}
                            >
                              设为默认
                            </Button>
                          )}
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDeleteBankCard(card.id)}
                          >
                            <i className="bi bi-trash me-1"></i>
                            删除
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          关闭
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProfileModal;
